<template>
    <DCard padding>
        <DRow align-vertical="end">
            <DCol
                sm="12"
                class="text-right"
            >
                <DButton
                    id="table-configuration"
                    icon="cog-outline"
                    :label="__('messages.list_configuration')"
                    class="float-end"
                    @click="showListViewModal = true"
                />
            </DCol>
        </DRow>
        <DRow
            class="mt-2 mb-2"
            align-vertical="end"
        >
            <DCol
                sm="2"
                class="pt-1"
            >
                <DInput
                    id="id"
                    v-model="filterData.id"
                    :label="__('customers.all_business_incidents.id')"
                />
            </DCol>
            <DCol
                sm="2"
                class="pt-1"
            >
                <DSelect
                    id="type"
                    v-model="filterData.type"
                    :label="__('customers.all_business_incidents.type')"
                    :options="businessIncidentTypeOptions"
                />
            </DCol>
            <DCol
                sm="2"
                class="pt-1"
            >
                <DSelect
                    id="customerRequest"
                    v-model="filterData.customerRequest"
                    :label="__('customers.all_business_incidents.customer_request')"
                    :options="businessIncidentCustomerRequestOptions"
                />
            </DCol>
            <DCol
                sm="2"
                class="pt-1"
            >
                <DSelect
                    id="incidentSolution"
                    v-model="filterData.incidentSolution"
                    :label="__('customers.all_business_incidents.incident_solution')"
                    :options="businessIncidentSolutionOptions"
                />
            </DCol>
            <DCol
                sm="2"
                class="pt-1"
            >
                <DSelect
                    id="status"
                    v-model="filterData.status"
                    :label="__('customers.all_business_incidents.status')"
                    :options="statusOptions"
                />
            </DCol>
            <DCol
                sm="2"
                class="pt-1"
            >
                <DInput
                    id="subject_id"
                    v-model="filterData.subjectId"
                    :label="__('customers.all_business_incidents.subject_id')"
                />
            </DCol>
        </DRow>
        <DRow
            class="mt-2 mb-2"
            align-vertical="end"
        >
            <DCol sm="2">
                <DMultiSelect
                    id="user"
                    :label="__('customers.all_business_incidents.user')"
                    :options="userOptions"
                    selection-type="text"
                    :selected="filterData.users"
                    @update="updateSelectedUsers"
                />
            </DCol>
            <DCol
                sm="3"
                class="d-flex gap-2 pt-2"
            >
                <DButton
                    :label="__('messages.actions.filter')"
                    @click="changePage(businessIncidents.current_page)"
                />
                <DButton
                    icon="filter-off"
                    :label="__('messages.actions.reset_filter')"
                    @click="resetFilters"
                />
            </DCol>
        </DRow>
        <DTable
            class="table-responsive"
            :items="items"
            :fields="getFields"
            :sorter="isSortable"
            :sorter-value="sorterValue"
            @update:sorter-value="updateOrderValue"
        >
            <template #id="{item}">
                <td>
                    <Link :href="incidentRoute(item)">
                        {{ item.id }}
                    </Link>
                </td>
            </template>
            <template #date="{item}">
                <td>
                    {{ item.date }}
                </td>
            </template>
            <template #type="{item}">
                <td>
                    {{ item.type ?? '' }}
                </td>
            </template>
            <template #customer_request="{item}">
                <td>
                    {{ item.customer_request ?? '' }}
                </td>
            </template>
            <template #incident_solution="{item}">
                <td>
                    {{ item.incident_solution ?? '' }}
                </td>
            </template>
            <template #customer_id="{item}">
                <td>
                    <Link :href="customerProfileRoute(item)">
                        {{ item.customer_id }}
                    </Link>
                </td>
            </template>
            <template #status="{item}">
                <td>
                    {{ __('customers.all_business_incidents.statuses.' + item.status) }}
                </td>
            </template>
            <template #contracts="{item}">
                <td>
                    <div
                        v-for="(id, index) in item.contracts"
                        :key="index"
                    >
                        <Link
                            v-if="userPermissions.includes('contract.view')"
                            :href="route('customers.customers.contracts.show', {customer: item.customer_id, contract: id})"
                        >
                            {{ id + (index < item.contracts.length - 1 ? ',' : ' ') }}
                        </Link>
                        <span
                            v-else
                        >{{ id + (index < item.contracts.length - 1 ? ',' : ' ') }}</span>
                    </div>
                </td>
            </template>
            <template #products="{item}">
                <td>
                    <div
                        v-for="(product, index) in item.products"
                        :key="index"
                    >
                        <Link
                            v-if="userPermissions.includes('product.view')"
                            :href="route('products.products.show', {product: product.id})"
                        >
                            {{ product.name + (index < item.contracts.length - 1 ? ',' : ' ') }}
                        </Link>
                        <span
                            v-else
                        >{{ product.name + (index < item.contracts.length - 1 ? ',' : ' ') }}</span>
                    </div>
                </td>
            </template>
        </DTable>
        <DPagination
            v-if="businessIncidents.last_page > 1"
            :active-page="businessIncidents.current_page"
            :pages="businessIncidents.last_page"
            align="center"
            @update:activePage="changePage"
        />
        <ListViewModal
            v-if="showListViewModal"
            :show-modal="showListViewModal"
            :user-list-view="userListView"
            @close-modal="showListViewModal = false"
        />
    </DCard>
</template>

<script setup>
import { router, usePage } from '@inertiajs/vue3'
import { route } from '~@/Composables/route'
import { computed, ref } from 'vue'
import { __ } from '~@/Composables/translation'
import ListViewModal from '~@/Components/ListView/ListViewModal'

const props = defineProps({
    businessIncidents: {
        type: Object,
        default: () => {
            return {}
        }
    },
    items: {
        type: Object,
        default: () => {
            return {}
        }
    },
    statuses: {
        type: Array,
        default: () => {
            return []
        }
    },
    businessIncidentTypes: {
        type: Array,
        default: () => {
            return []
        }
    },
    businessIncidentCustomerRequests: {
        type: Array,
        default: () => {
            return []
        }
    },
    businessIncidentSolutions: {
        type: Array,
        default: () => {
            return []
        }
    },
    filters: {
        type: Array,
        default: () => {
            return []
        }
    },
    userListView: {
        type: Array,
        default: () => {
            return []
        }
    },
    users: {
        type: Array,
        default () {
            return []
        }
    }
})

const filterData = {
    id: props.filters.id ?? null,
    status: props.filters.status ?? null,
    type: props.filters.type ?? null,
    customerRequest: props.filters.customerRequest ?? null,
    incidentSolution: props.filters.incidentSolution ?? null,
    subjectId: props.filters.subjectId ?? null,
    users: props.filters.users ?? [],
    orderBy: props.filters.orderBy ?? 'id',
    orderByAsc: getSortDirection(),
    saveFilters: 0
}

const statusOptions = computed(function () {
    const statuses = props.statuses.map(status => {
        return {
            value: status.value,
            label: status.text
        }
    })

    statuses.unshift({
        value: '',
        label: ''
    })

    return statuses
})

const businessIncidentTypeOptions = computed(function () {
    const types = props.businessIncidentTypes.map(item => {
        return {
            value: item.value,
            label: item.text
        }
    })

    types.unshift({
        value: null,
        label: ''
    })

    return types
})

const businessIncidentCustomerRequestOptions = computed(function () {
    const customerRequests = props.businessIncidentCustomerRequests.map(item => {
        return {
            value: item.value,
            label: item.text
        }
    })

    customerRequests.unshift({
        value: null,
        label: ''
    })

    return customerRequests
})

const businessIncidentSolutionOptions = computed(function () {
    const incidentSolutions = props.businessIncidentSolutions.map(item => {
        return {
            value: item.value,
            label: item.text
        }
    })

    incidentSolutions.unshift({
        value: null,
        label: ''
    })

    return incidentSolutions
})

const userPermissions = computed(() => usePage().props.auth.permissions)
const showListViewModal = ref(false)
const getFields = computed(() => {
    const availableList = []

    props.userListView.forEach((item) => {
        if (item.show_column === true) {
            availableList.push({
                key: item.column,
                label: __('settings.list_view.' + item.list + '.' + item.column),
                sorter: item.sort_column_enabled,
                _style: item.width !== null ? { minWidth: item.width + 'px', maxWidth: item.width + 'px' } : {}
            })
        }
    })

    return availableList
})

const userOptions = computed(() => {
    return props.users.map((item) => {
        return { value: item.value, text: item.label, selected: filterData?.users?.includes(String(item.value)) }
    })
})

const isSortable = computed(() => {
    if (!sorterValue.value.column) return false

    return { external: true }
})

const sorterValue = computed(() => {
    return {
        column: filterData.orderBy,
        asc: filterData.orderByAsc === 'asc' ? 1 : 0
    }
})
const updateOrderValue = (event) => {
    filterData.orderBy = event.column
    filterData.orderByAsc = event.asc ? 'asc' : 'desc'

    changePage(props.businessIncidents.current_page)
}

const changePage = (page) => {
    const query = Object.fromEntries(Object.entries(filterData).filter(([_, v]) => v))

    if (page > 1) {
        query.page = page
    }

    router.get(route('customers.all-business-incidents.index', {
        saveFilters: true
    }), query)
}

const resetFilters = () => {
    router.get(route('customers.all-business-incidents.index', {
        saveFilters: 1,
        defaultFilters: 1
    }))
}

function getSortDirection () {
    if (props.filters.orderByAsc === null) {
        return null
    }

    return props.filters.orderByAsc === 'asc' ? 'asc' : 'desc'
}

function incidentRoute (item) {
    return route('customers.customers.business-incidents.edit', { customer: item.customer_id, business_incident: item.id })
}

function customerProfileRoute (item) {
    return route('customers.customers.show', { customer: item.customer_id })
}

function updateSelectedUsers (values) {
    filterData.users = values
}

</script>

<style lang="scss" scoped>
.ul-class{
    margin: 0;
    padding-left: 15px;
}
</style>
